import { FileTextIcon } from 'lucide-react'
import { Link } from 'react-router-dom'

import type { InvoiceLineItem, MemoriaDeCalculo } from '@nebula/shared/services'
import { formatMoney, linhasDaMemoriaDoVoucher, rotuloDaCompetencia } from '@nebula/shared/utils'

/**
 * A memória de cálculo de um lançamento por competência (#1091 F2a), em frases:
 * - fixo do bloco: uma linha por revisão, "Revisão N: R$ preço × dias/dias do mês dias";
 * - excedente: "Excedente em N dias: R$ total".
 */
export function linhasDaMemoria(
  calculo: MemoriaDeCalculo | null | undefined,
  currency = 'BRL',
): string[] {
  if (!calculo) return []
  // voucher (#1107): cobrança, cobertura, virada… — a régua do shared
  if (calculo.tipo?.startsWith('voucher')) return linhasDaMemoriaDoVoucher(calculo, currency)
  if (calculo.tipo === 'excedente') {
    return [
      `Excedente em ${calculo.dias ?? 0} dias: ${formatMoney(Number(calculo.total ?? 0), currency)}`,
    ]
  }
  return (calculo.revisoes ?? []).map(
    (r) =>
      `Revisão ${r.numero}: ${formatMoney(Number(r.preco ?? 0), currency)} × ${r.dias}/${calculo.dias_do_mes} dias`,
  )
}

/**
 * O que a linha da fatura ganha quando veio de um contrato especial (#1091 F3, K5/K8): a
 * competência, a memória de cálculo e o link ao detalhe do contrato na aba Contratos.
 * Linha sem nada disso não renderiza nada.
 */
const InvoiceLineContract = ({ linha }: { linha: InvoiceLineItem }) => {
  const memoria = linhasDaMemoria(linha.calculo, linha.currency)
  if (!linha.competence_period && !linha.special_contract_id && memoria.length === 0) return null

  return (
    <div className="mt-1 space-y-0.5 text-xs text-gray-500">
      {linha.competence_period && (
        <div>Competência {rotuloDaCompetencia(linha.competence_period)}</div>
      )}
      {memoria.map((m) => (
        <div key={m}>{m}</div>
      ))}
      {linha.special_contract_id && (
        <Link
          to={{ search: `?tab=contracts&contrato=${linha.special_contract_id}` }}
          className="inline-flex items-center gap-1 text-blue-600 hover:underline"
        >
          <FileTextIcon className="size-3" />
          Contrato: {linha.special_contract_name || 'contrato especial'}
        </Link>
      )}
    </div>
  )
}

export default InvoiceLineContract
